import { requestPrivateAudio } from "./collectionV2";
import { getAudioPermission, savePrivateRecording, RAW_AUDIO_CHANGED } from "./rawAudio";

// Foreground permission is memory-only. It is never restored with a saved game.
export const AUDIO_SESSION_CHANGED = "pure-audio-session-changed";
let gameId = "HOME", generation = 0, choice = null, grant = null, expiryTimer = null;
let message = "";
const uploads = new Set();
const visible = () => document.visibilityState === "visible";
export const supportsAudioStorage = game => !!game && !["HOME", "BRIDGE", "CONVO-BRIDGE", "CONVO_BRIDGE", "UNKNOWN"].includes(game.toUpperCase());
const notify = () => window.dispatchEvent(new Event(AUDIO_SESSION_CHANGED));
const endRemote = session => {
    if (session) requestPrivateAudio(`sessions/${session.audio_session_id}`, { method: "DELETE", keepalive: true }).catch(() => {
        // Closing a page/offline device cannot guarantee delivery; server expiry is the backstop.
    });
};
export const resetAudioSession = () => {
    const previous = grant;
    generation++; grant = null; choice = null;
    clearTimeout(expiryTimer); expiryTimer = null;
    for (const controller of uploads) controller.abort();
    uploads.clear();
    endRemote(previous?.session);
    notify();
};
export const selectAudioGame = game => {
    if (game !== gameId) { resetAudioSession(); gameId = game; message = ""; notify(); }
};
export const audioSessionState = () => ({ gameId, choice, grant, message, visible: visible() });
export const declineSessionStorage = () => {
    resetAudioSession(); choice = "declined"; message = "Recording storage is off for this game session."; notify();
};
export const confirmSessionStorage = async policy => {
    const receipt = getAudioPermission();
    if (!visible() || !supportsAudioStorage(gameId) || !policy?.enabled || receipt?.policy_sha256 !== policy.policy_sha256) {
        throw new Error("Current recording permission and a visible, eligible game are required.");
    }
    const epoch = generation, selected = gameId;
    const session = await requestPrivateAudio("sessions", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ audio_consent_id: receipt.audio_consent_id, game_id: selected,
            automatic_storage_confirmed: true, own_voice_only_confirmed: true }) });
    if (epoch !== generation || !visible() || getAudioPermission()?.audio_consent_id !== receipt.audio_consent_id) {
        endRemote(session); throw new Error("The game session changed. Please confirm again after returning.");
    }
    const remaining = Date.parse(session.expires_at) - Date.now();
    if (!/^asn_[0-9a-f]{32}$/.test(session.audio_session_id || "") || session.game_id !== selected
        || session.participant_id !== receipt.participant_id || session.audio_consent_id !== receipt.audio_consent_id
        || !(remaining > 0 && remaining <= 12 * 60 * 60 * 1000)) {
        endRemote(session); throw new Error("Recording-session receipt could not be verified.");
    }
    grant = { receipt, session, gameId: selected, generation: epoch };
    choice = "authorized"; message = "Completed microphone recordings will be stored privately for this game session.";
    clearTimeout(expiryTimer);
    expiryTimer = setTimeout(() => { resetAudioSession(); message = "Recording-session permission expired. Choose again."; notify(); }, remaining);
    notify();
};
export const getRecordingSession = game => {
    if (!visible() || !supportsAudioStorage(game) || !grant || game !== gameId || grant.gameId !== game
        || grant.generation !== generation || !(Date.parse(grant.session.expires_at) > Date.now())
        || getAudioPermission()?.audio_consent_id !== grant.receipt.audio_consent_id) return null;
    return grant;
};
export const retainCompletedRecording = async (started, blob) => {
    // Both start and completion must be inside the SAME foreground authorization.
    if (!started || getRecordingSession(started.gameId) !== started) return;
    const controller = new AbortController(); uploads.add(controller);
    try {
        const saved = await savePrivateRecording({ ...started, blob, signal: controller.signal });
        if (getRecordingSession(started.gameId) === started) {
            message = `Privately stored as ${saved.audio_id}; expires ${saved.expires_at}. Not publicly released.`;
        }
    } catch (error) {
        if (getRecordingSession(started.gameId) === started) {
            message = `Recording storage could not be confirmed: ${error.message || "request failed"}. Check Show my recording records. No automatic retry is queued.`;
        }
    } finally { uploads.delete(controller); notify(); }
};
export const watchAudioLifecycle = () => {
    const visibility = () => { if (!visible()) resetAudioSession(); else notify(); };
    const storage = event => { if (!event.key || event.key.startsWith("pure_collection_") || event.key.startsWith("pureversation_consent")) resetAudioSession(); };
    document.addEventListener("visibilitychange", visibility);
    document.addEventListener("freeze", resetAudioSession);
    window.addEventListener("pagehide", resetAudioSession);
    window.addEventListener("pageshow", resetAudioSession);
    window.addEventListener(RAW_AUDIO_CHANGED, resetAudioSession);
    window.addEventListener("storage", storage);
    return () => {
        document.removeEventListener("visibilitychange", visibility);
        document.removeEventListener("freeze", resetAudioSession);
        window.removeEventListener("pagehide", resetAudioSession);
        window.removeEventListener("pageshow", resetAudioSession);
        window.removeEventListener(RAW_AUDIO_CHANGED, resetAudioSession);
        window.removeEventListener("storage", storage);
        resetAudioSession();
    };
};
